import { captureMonitorException, captureMonitorMessage } from '@/utils/monitor/core/client'
import { MONITOR_EVENT, MONITOR_PATH, MONITOR_SOURCE, getMonitorDescriptor } from '@/utils/monitor/schema'

// socket warning 类事件的冷却时间，避免断线抖动时重复刷告警。
const SOCKET_WARNING_COOLDOWN_MS = 30000
// socket error 类事件的冷却时间，避免同一异常在短时间内连续重复上报。
const SOCKET_ERROR_COOLDOWN_MS = 15000
const SOCKET_MONITOR_PATH = MONITOR_PATH.SOCKET_INFRA

// 统一生成 socket 事件 tags，收敛到统一监控字段。
const getSocketBaseTags = (monitorEvent) => {
  return getMonitorDescriptor({
    monitorSource: MONITOR_SOURCE.SOCKET,
    monitorEvent,
    monitorPath: SOCKET_MONITOR_PATH,
  })
}

// 统一生成 socket 事件 extra，方便后续补充更多上下文。
const getSocketBaseExtra = (client, extra = {}) => {
  return {
    socket_client: client?.logPrefix ?? 'socket',
    socket_url: client?.url?.value ?? client?.baseUrl ?? '',
    ...extra,
  }
}

// 只截取一小段原始消息，避免把大包体直接打进监控。
const getRawDataPreview = (rawData) => {
  if (rawData === undefined || rawData === null) {
    return ''
  }

  if (typeof rawData === 'string') {
    return rawData.slice(0, 300)
  }

  try {
    return JSON.stringify(rawData).slice(0, 300)
  } catch {
    return String(rawData).slice(0, 300)
  }
}

// socket 非主动断开时上报 warning，帮助定位链路不稳定问题。
export const reportSocketDisconnected = ({ client, event }) => {
  const socketCode = event?.code ?? 'unknown'
  const monitor = getSocketBaseTags(MONITOR_EVENT.SOCKET_DISCONNECTED)

  captureMonitorMessage(monitor.title, {
    level: 'warning',
    dedupeKey: `socket:disconnect:${client?.logPrefix}:${socketCode}`,
    cooldownMs: SOCKET_WARNING_COOLDOWN_MS,
    fingerprint: [monitor.title, client?.logPrefix ?? 'socket', String(socketCode)],
    tags: monitor.tags,
    extra: getSocketBaseExtra(client, {
      socket_code: String(socketCode),
      socket_reason: event?.reason ?? '',
    }),
  })
}

// 浏览器底层 socket error 事件统一走 warning。
export const reportSocketError = ({ client, event }) => {
  const monitor = getSocketBaseTags(MONITOR_EVENT.SOCKET_ERROR)

  captureMonitorMessage(monitor.title, {
    level: 'warning',
    dedupeKey: `socket:error:${client?.logPrefix}`,
    cooldownMs: SOCKET_WARNING_COOLDOWN_MS,
    fingerprint: [monitor.title, client?.logPrefix ?? 'socket'],
    tags: monitor.tags,
    extra: getSocketBaseExtra(client, {
      socket_error_type: event?.type ?? '',
    }),
  })
}

// 自动重连彻底失败说明链路已经明显异常，提升为 error 级别。
export const reportSocketReconnectFailed = ({ client }) => {
  const monitor = getSocketBaseTags(MONITOR_EVENT.SOCKET_RECONNECT_FAILED)

  captureMonitorMessage(monitor.title, {
    level: 'error',
    dedupeKey: `socket:reconnect_failed:${client?.logPrefix}`,
    cooldownMs: SOCKET_WARNING_COOLDOWN_MS,
    fingerprint: [monitor.title, client?.logPrefix ?? 'socket'],
    tags: monitor.tags,
    extra: getSocketBaseExtra(client),
  })
}

// 原始消息解析失败是明确异常，直接走 captureException。
export const reportSocketMessageParseError = ({ client, error, rawData }) => {
  const monitor = getSocketBaseTags(MONITOR_EVENT.SOCKET_PARSE_ERROR)

  captureMonitorException(error, {
    dedupeKey: `socket:message_parse:${client?.logPrefix}`,
    cooldownMs: SOCKET_ERROR_COOLDOWN_MS,
    tags: monitor.tags,
    extra: getSocketBaseExtra(client, {
      raw_data_preview: getRawDataPreview(rawData),
    }),
  })
}

// 业务回调执行报错同样属于异常，记录 topic 帮助定位是哪条订阅链路出错。
export const reportSocketEmitError = ({ client, eventKey, error }) => {
  const monitor = getSocketBaseTags(MONITOR_EVENT.SOCKET_EMIT_ERROR)

  captureMonitorException(error, {
    dedupeKey: `socket:emit:${client?.logPrefix}:${eventKey}`,
    cooldownMs: SOCKET_ERROR_COOLDOWN_MS,
    tags: monitor.tags,
    extra: getSocketBaseExtra(client, {
      socket_topic: eventKey,
    }),
  })
}
